import * as THREE from 'three'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'
import * as BGU from 'three/examples/jsm/utils/BufferGeometryUtils.js'

const stlLoader   = new STLLoader()

/**
 * @param {File} stlFile
 * @returns {Promise<{ stlData: { size_unitless: object, volume_unitless: number, area_unitless: number } }>}
 */
export const getStlGeometryData = (stlFile) => {
	const fileUrl = URL.createObjectURL(stlFile)
	return new Promise((resolve, reject) => {
		stlLoader.load(fileUrl,
			(geometry) => resolve({ stlData: calculateGeometryProperties(geometry) }),
			undefined,
			(error) => reject(error),
		)
	})
}

const calculateGeometryProperties = (geometry) => {
	let geo = geometry.clone()
	if (!geo.index) {
		const count = geo.getAttribute('position').count
		const indices = new Uint32Array(count)
		for (let i = 0; i < count; i++) indices[i] = i
		geo.setIndex(new THREE.BufferAttribute(indices, 1))
	}
	geo = BGU.mergeVertices(geo)
	geo.computeVertexNormals()
	geo = geo.toNonIndexed()
	geo.computeBoundingBox()
	const bb = geo.boundingBox
	if (!bb) throw 'No bounding box'

	const size_unitless = { x: bb.max.x - bb.min.x, y: bb.max.y - bb.min.y, z: bb.max.z - bb.min.z }
	const pos = geo.getAttribute('position')
	const seen = new Set()
	/** @type {[THREE.Vector3, THREE.Vector3, THREE.Vector3][]} */
	const tris = []
	for (let i = 0; i < pos.count; i += 3) {
		const key = `${i}-${i+1}-${i+2}`
		if (seen.has(key)) continue
		seen.add(key)
		tris.push([
			new THREE.Vector3().fromBufferAttribute(pos, i),
			new THREE.Vector3().fromBufferAttribute(pos, i + 1),
			new THREE.Vector3().fromBufferAttribute(pos, i + 2),
		])
	}

	const volume_unitless = Math.abs(tris.reduce((acc, [a, b, c]) =>
		acc + new THREE.Vector3().copy(b).cross(c).dot(a) / 6.0, 0))

	const area_unitless = tris.reduce((acc, [a, b, c]) => {
		const v1 = new THREE.Vector3().subVectors(b, a)
		const v2 = new THREE.Vector3().subVectors(c, a)
		return acc + new THREE.Vector3().crossVectors(v1, v2).length() / 2
	}, 0)

	return { size_unitless, volume_unitless, area_unitless }
}
